import React, { useEffect, useState } from "react";
import { Loader2, Mail, Save, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const inputCls =
    "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green";

const Field = ({ label, hint, children }) => (
    <div>
        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
            {label}
        </label>
        {children}
        {hint && <p className="mt-1 text-[11px] text-brand-ink-soft">{hint}</p>}
    </div>
);

export default function AdminSettings({ apiAuth }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        apiAuth("/admin/settings")
            .then((r) => setForm(r.data))
            .catch(() => toast.error("Não foi possível carregar as definições"));
    }, [apiAuth]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                smtp_host: form.smtp_host || null,
                smtp_port: form.smtp_port ? parseInt(form.smtp_port, 10) : null,
                smtp_security: form.smtp_security || null,
                smtp_user: form.smtp_user || null,
                smtp_from: form.smtp_from || null,
                alert_email: form.alert_email || null,
            };
            if (form.smtp_password) payload.smtp_password = form.smtp_password;
            const { data } = await apiAuth("/admin/settings", {
                method: "PUT",
                data: payload,
            });
            setForm(data);
            toast.success("Definições guardadas");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao guardar");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const { data } = await apiAuth("/admin/settings/smtp-test", {
                method: "POST",
            });
            toast.success(`Email de teste enviado para ${data.sent_to}`);
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Falha no envio de teste");
        } finally {
            setTesting(false);
        }
    };

    if (!form) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl" data-testid="admin-settings-panel">
            <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                <div className="px-6 py-5 bg-brand-ink text-white flex items-center gap-3">
                    <Mail className="h-5 w-5 text-brand-orange" />
                    <div>
                        <h2 className="font-display font-medium text-xl">
                            Configurador SMTP — Alertas por email
                        </h2>
                        <p className="text-xs text-white/60 mt-0.5">
                            Quando chega um pedido de kit de amostras grátis, a equipa
                            recebe um email automático.
                        </p>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <div className="grid sm:grid-cols-[1fr_130px_140px] gap-3">
                        <Field label="Servidor SMTP *" hint="Ex: smtp.gmail.com, smtp-mail.outlook.com">
                            <input
                                value={form.smtp_host || ""}
                                onChange={set("smtp_host")}
                                placeholder="smtp.exemplo.pt"
                                className={inputCls}
                                data-testid="smtp-field-host"
                            />
                        </Field>
                        <Field label="Porta" hint="587 (TLS) / 465 (SSL)">
                            <input
                                type="number"
                                value={form.smtp_port || ""}
                                onChange={set("smtp_port")}
                                className={inputCls}
                                data-testid="smtp-field-port"
                            />
                        </Field>
                        <Field label="Segurança">
                            <select
                                value={form.smtp_security || "tls"}
                                onChange={set("smtp_security")}
                                className={inputCls}
                                data-testid="smtp-field-security"
                            >
                                <option value="tls">STARTTLS</option>
                                <option value="ssl">SSL</option>
                                <option value="none">Nenhuma</option>
                            </select>
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Utilizador" hint="Normalmente o endereço de email">
                            <input
                                value={form.smtp_user || ""}
                                onChange={set("smtp_user")}
                                placeholder="alertas@saborliquido.pt"
                                className={inputCls}
                                data-testid="smtp-field-user"
                            />
                        </Field>
                        <Field
                            label="Password / App password"
                            hint={
                                form.smtp_password_set
                                    ? "Guardada ✓ — deixa vazio para manter"
                                    : "No Gmail usa uma App Password"
                            }
                        >
                            <input
                                type="password"
                                value={form.smtp_password || ""}
                                onChange={set("smtp_password")}
                                placeholder={form.smtp_password_set ? "••••••••" : ""}
                                className={inputCls}
                                data-testid="smtp-field-password"
                            />
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Remetente (From)" hint="Aparece como origem dos alertas">
                            <input
                                value={form.smtp_from || ""}
                                onChange={set("smtp_from")}
                                placeholder="Sabor Líquido <alertas@saborliquido.pt>"
                                className={inputCls}
                                data-testid="smtp-field-from"
                            />
                        </Field>
                        <Field label="Email da equipa *" hint="Quem recebe os alertas de amostras">
                            <input
                                value={form.alert_email || ""}
                                onChange={set("alert_email")}
                                placeholder="parceiros@saborliquido.pt"
                                className={inputCls}
                                data-testid="smtp-field-alert-email"
                            />
                        </Field>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            data-testid="smtp-save-button"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-[#2C312E] text-white px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Guardar definições
                        </button>
                        <button
                            type="button"
                            onClick={sendTest}
                            disabled={testing}
                            data-testid="smtp-test-button"
                            className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-brand-green/20 hover:border-brand-green text-brand-ink px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                        >
                            {testing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" />
                            )}
                            Enviar email de teste
                        </button>
                    </div>

                    <div className="flex items-start gap-2 rounded-2xl bg-brand-cream/70 p-4 text-xs text-brand-ink-soft">
                        <ShieldCheck className="h-4 w-4 shrink-0 text-brand-green mt-0.5" />
                        A password fica guardada no servidor e nunca é devolvida ao
                        browser. Guarda primeiro as definições e usa depois o botão de
                        teste para validar a configuração.
                    </div>
                </div>
            </div>
        </div>
    );
}
